import { createAction } from '@reduxjs/toolkit';

import {
    AGENT_ACTIVITY_ACTIONS,
    type CreationStage,
    type SceneFillKind,
} from './agent-activity-types';

// Starts tracking a freshly-created scene, which reads as "building" until the kinds
// of content it needs have landed.
export const startSceneBuilding = createAction<{
    sceneId: string;
    needsText: boolean;
    needsMedia: boolean;
    needsIcon: boolean;
}>(AGENT_ACTIVITY_ACTIONS.START_SCENE_BUILDING);

export const recordSceneFill = createAction<{
    sceneId: string;
    kind: SceneFillKind;
}>(AGENT_ACTIVITY_ACTIONS.RECORD_SCENE_FILL);

// Drops build tracking for every scene, unblurring any that never received the fills
// they were waiting on. Narrower than clearAllAgentActivity: scenes stay marked as
// touched this turn, so the working indicator survives. Dispatched once the build is
// far enough along that an unfilled scene is finished rather than pending.
export const clearAllSceneBuilding = createAction(AGENT_ACTIVITY_ACTIONS.CLEAR_ALL_SCENE_BUILDING);

// One-way on purpose: it is never unset per tool call, because consecutive tool calls
// land milliseconds apart and unsetting between them flickers the indicator. Only
// clearAllAgentActivity takes a scene back out.
export const markSceneTouchedByAgent = createAction<{
    sceneIds: string[];
}>(AGENT_ACTIVITY_ACTIONS.MARK_SCENE_TOUCHED_BY_AGENT);

// A true bracket, unlike the mark above — the vision call is long-running and async,
// so there's no flicker risk and the narrower window is more informative.
export const setSceneReviewing = createAction<{
    sceneIds: string[];
    isReviewing: boolean;
}>(AGENT_ACTIVITY_ACTIONS.SET_SCENE_REVIEWING);

// Blurs a scene that is otherwise just "edited": voicedriven base scenes carry the
// voice asset rather than editable text/media, so they never receive a fill and can't
// be tracked as building. They stay blurred until the turn ends. One-way like
// markSceneTouchedByAgent, for the same anti-flicker reason.
export const markSceneBlurred = createAction<{
    sceneIds: string[];
}>(AGENT_ACTIVITY_ACTIONS.MARK_SCENE_BLURRED);

export const markScenesPendingAgent = createAction<{
    sceneIds: string[];
}>(AGENT_ACTIVITY_ACTIONS.MARK_SCENES_PENDING_AGENT);

export const clearScenesPendingAgent = createAction<{
    sceneIds: string[];
}>(AGENT_ACTIVITY_ACTIONS.CLEAR_SCENES_PENDING_AGENT);

// The user asked to stop reviewing and take the video as-is. Scoped to the current turn:
// clearAllAgentActivity unsets it, so the next build reviews normally.
export const skipReview = createAction(AGENT_ACTIVITY_ACTIONS.SKIP_REVIEW);

// Moves the from-scratch build onto its next visible stage. Monotonic — the reducer
// ignores a stage that would move backwards — so tools can announce their stage
// unconditionally without ordering themselves against each other.
export const enterCreationStage = createAction<{
    stage: CreationStage;
}>(AGENT_ACTIVITY_ACTIONS.ENTER_CREATION_STAGE);

// Wipes every slice: building progress, touched-this-turn, and reviewing. Separate
// from clearAllSceneBuilding, which only drops build progress, because a scene can
// finish building long before the agent is done with it.
export const clearAllAgentActivity = createAction(AGENT_ACTIVITY_ACTIONS.CLEAR_ALL_AGENT_ACTIVITY);
